import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '@chakra-ui/react';
import { FiAward, FiPlus, FiTrash2 } from 'react-icons/fi';

const Badges = () => {
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const toast = useToast();

  const fetchBadges = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/v1/admin/badges');
      setBadges(res.data?.data || res.data || []);
    } catch (err) {
      // Fallback mock data if endpoint doesn't exist yet
      setBadges([
        { id: '1', name: 'VIP Supporter', description: 'Awarded to top supporters', iconUrl: '' },
        { id: '2', name: 'Verified Star', description: 'Awarded to verified users', iconUrl: '' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBadges();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/v1/admin/badges', { name, description, iconUrl });
      toast({ title: 'Badge created successfully', status: 'success' });
      setName('');
      setDescription('');
      setIconUrl('');
      fetchBadges();
    } catch (err) {
      toast({ title: 'Failed to create badge', description: err.response?.data?.message || err.message, status: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this badge?')) return;
    try {
      await axios.delete(`/api/v1/admin/badges/${id}`);
      toast({ title: 'Badge deleted', status: 'success' });
      fetchBadges();
    } catch (err) {
      toast({ title: 'Failed to delete badge', status: 'error' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <FiAward className="text-cyan-400" /> User Badges Management
        </h1>
      </div>

      <div className="bg-slate-900/60 border border-cyan-500/20 rounded-2xl p-6 backdrop-blur-xl">
        <h2 className="text-lg font-semibold text-cyan-200 mb-4">Create New Badge</h2>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Badge Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="bg-slate-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-400"
          />
          <input
            type="text"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="bg-slate-950/70 border border-cyan-500/30 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-400"
          />
          <button
            type="submit"
            className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl px-6 py-2.5 flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 hover:opacity-90 transition"
          >
            <FiPlus /> Add Badge
          </button>
        </form>
      </div>

      <div className="bg-slate-900/60 border border-cyan-500/20 rounded-2xl p-6 backdrop-blur-xl">
        <h2 className="text-lg font-semibold text-cyan-200 mb-4">Existing Badges</h2>
        {loading ? (
          <p className="text-cyan-300/60 text-sm">Loading badges...</p>
        ) : badges.length === 0 ? (
          <p className="text-cyan-300/60 text-sm">No badges found.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {badges.map((badge) => (
              <div key={badge.id || badge._id} className="bg-slate-950/50 border border-cyan-500/20 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-white">{badge.name}</h3>
                  <p className="text-xs text-slate-400">{badge.description}</p>
                </div>
                <button
                  onClick={() => handleDelete(badge.id || badge._id)}
                  className="text-red-400 hover:text-red-300 p-2 rounded-lg bg-red-500/10 transition"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Badges;
